import apiClient from './client';

export interface CartLine {
    id: number;
    variant: number;
    variant_name: string;
    quantity: number;
    unit_price: string;
    line_total: string;
}

export interface Cart {
    id: number;
    lines: CartLine[];
    total: string;
    item_count: number;
    updated_at: string;
}

export interface CheckoutPayload {
    customer_name: string;
    customer_phone: string;
    shipping_city: string;
    shipping_address?: string;
    shipping_country?: string;
    payment_method: 'cod' | 'card' | 'bank';
    coupon_code?: string;
}

export interface SalesOrder {
    id: number;
    number: string;
    customer_name: string;
    customer_phone: string;
    status: string;
    total: string;
    subtotal: string;
    discount_amount?: string;
    tax_amount: string;
    payment_method: string;
    payment_status: string;
    coupon_code?: string;
    loyalty_points_earned?: number;
    amount_paid?: string;
    amount_remaining?: string;
    notes: string;
    lines: SalesOrderLine[];
    created_at: string;
    confirmed_at: string | null;
    is_pos_order: boolean;
    invoice_count?: number;
    delivery_order_count?: number;
    accounting_invoices_url?: string;
    inventory_deliveries_url?: string;
}

export interface SalesOrderLine {
    id: number;
    variant: number;
    product_name: string;
    sku: string;
    quantity: number;
    unit_price: string;
    discount_percent: string;
    tax_rate?: string;
    description?: string;
    uom?: string;
    line_total: string;
}

export interface SalesReturnLinePayload {
    line_id: number;
    quantity: number;
}

export interface VendorOrderCreatePayload {
    customer_name: string;
    customer_phone?: string;
    customer_email?: string;
    notes?: string;
    payment_method?: 'cod' | 'card' | 'bank';
}

export interface AddOrderLinePayload {
    variant_id: number;
    quantity: number;
    unit_price?: string;
    discount_percent?: string;
}

import { tokenStore } from './client';

export const salesApi = {
    getCart: async () => {
        if (!tokenStore.getAccess()) return null;
        const { data } = await apiClient.get<Cart>('/sales/cart/');
        return data;
    },

    addToCart: async (variantId: number, quantity: number = 1) => {
        const { data } = await apiClient.post<Cart>('/sales/cart/add/', { variant_id: variantId, quantity });
        return data;
    },

    removeFromCart: async (lineId: number) => {
        const { data } = await apiClient.delete<Cart>(`/sales/cart/remove/${lineId}/`);
        return data;
    },

    checkout: async (payload: CheckoutPayload) => {
        const { data } = await apiClient.post<any>('/sales/cart/checkout/', payload);
        return data;
    },

    validateCheckoutCoupon: async (payload: {
        code: string;
        subtotal: string | number;
        country_code?: string;
        city?: string;
        applies_to?: string;
    }) => {
        const { data } = await apiClient.post<any>('/promotions/validate-coupon/', {
            applies_to: 'parts',
            ...payload,
        }, { skipAuth: true } as any);
        return data;
    },

    clearCart: async () => {
        const { data } = await apiClient.delete<{ status: string }>('/sales/cart/clear/');
        return data;
    },

    // ── Vendor-initiated Sales Orders ─────────────────────────────────────────
    /** Get full list of sales orders scoped to the vendor's store */
    getVendorSalesOrders: async (): Promise<SalesOrder[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get<any>('/sales/orders/');
        return (data as any).results ?? data ?? [];
    },

    /** Get a single sales order with full line details */
    getSalesOrderDetail: async (id: number): Promise<SalesOrder> => {
        const { data } = await apiClient.get<SalesOrder>(`/sales/orders/${id}/`);
        return data;
    },

    /** Create a new vendor-initiated draft sales order */
    createSalesOrder: async (payload: VendorOrderCreatePayload): Promise<SalesOrder> => {
        const { data } = await apiClient.post<SalesOrder>('/sales/orders/', payload);
        return data;
    },

    /** Add a product line to a draft sales order */
    addSalesOrderLine: async (orderId: number, payload: AddOrderLinePayload): Promise<SalesOrder> => {
        const { data } = await apiClient.post<SalesOrder>(`/sales/orders/${orderId}/add-line/`, payload);
        return data;
    },

    /** Confirm a draft sales order — deducts stock */
    confirmSalesOrder: async (orderId: number): Promise<SalesOrder> => {
        const { data } = await apiClient.post<SalesOrder>(`/sales/orders/${orderId}/confirm/`);
        return data;
    },

    /** Validate delivery and deduct reserved stock */
    deliverSalesOrder: async (orderId: number): Promise<SalesOrder> => {
        const { data } = await apiClient.post<SalesOrder>(`/sales/orders/${orderId}/deliver/`);
        return data;
    },

    updateSalesOrderStatus: async (orderId: number, status: string): Promise<{ status: string }> => {
        const { data } = await apiClient.post<{ status: string }>(`/sales/orders/${orderId}/update-status/`, { status });
        return data;
    },

    cancelSalesOrder: async (orderId: number, actingSalespersonId?: number) => {
        const payload = actingSalespersonId ? { acting_salesperson_id: actingSalespersonId } : {};
        const { data } = await apiClient.post(`/sales/orders/${orderId}/cancel/`, payload);
        return data;
    },

    resetSalesOrderToDraft: async (orderId: number, actingSalespersonId?: number) => {
        const payload = actingSalespersonId ? { acting_salesperson_id: actingSalespersonId } : {};
        const { data } = await apiClient.post(`/sales/orders/${orderId}/reset-to-draft/`, payload);
        return data;
    },

    deleteSalesOrder: async (orderId: number, actingSalespersonId?: number) => {
        const suffix = actingSalespersonId ? `?acting_salesperson_id=${actingSalespersonId}` : '';
        await apiClient.delete(`/sales/orders/${orderId}/${suffix}`);
    },

    createSalesBackorder: async (orderId: number, lines?: SalesReturnLinePayload[]) => {
        const payload = lines && lines.length > 0 ? { lines } : {};
        const { data } = await apiClient.post<{ backorder_id: number; backorder_number: string }>(`/sales/orders/${orderId}/create-backorder/`, payload);
        return data;
    },

    returnSalesOrderItems: async (
        orderId: number,
        payload?: { lines?: SalesReturnLinePayload[]; reason?: string; refund_method?: 'cash' | 'card' | 'store_credit'; create_backorder?: boolean }
    ) => {
        const { data } = await apiClient.post(`/sales/orders/${orderId}/return-items/`, payload || {});
        return data;
    },

    /** Create an invoice record from a confirmed sales order */
    createInvoiceFromOrder: async (orderId: number): Promise<{ invoice_id: string; amount: string; status: string }> => {
        const { data } = await apiClient.post<any>(`/sales/orders/${orderId}/create-invoice/`);
        return data;
    },
};
